import { FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import {
  Backdrop,
  Card,
  CardContent,
  Typography,
  Box,
  SvgIcon,
  Stack,
  MenuItem,
  Button as MuiButton,
  ButtonGroup,
  CircularProgress,
  Menu,
  ListItemIcon,
} from "@mui/material";
import {
  Save,
  ArrowDropDown,
  History,
  BarChart,
  Handshake,
  Search,
  FormatListBulleted,
} from "@mui/icons-material";
import {
  mdiChevronDown,
  mdiClockOutline,
  mdiMapMarkerRadiusOutline,
  mdiStoreOutline,
} from "@mdi/js";
import { Icon } from "@mdi/react";
import { useEditLeadForm } from "./hook";
import { User } from "../../model/User";
import { LeadInfoDialog } from "../LeadInfoDialog";
import { CoverageDealers } from "../CoverageDealers";
import { LookForwardModal } from "../LookForwardModal";
import { CustomSnackbar } from "../CustomSnackbar";
import { EditLeadNewFieldDialog } from "../EditLeadNewFieldsDialog";
import { ViewChangesDialog } from "../ViewChangesDialog";
import { CallDetailHistory } from "../ViewChangesDialog/types";
import { PersonalInformation } from "./sections/PersonalInformation";
import { SaleInformation } from "./sections/SaleInformation";
import { AdministrativeInformation } from "./sections/AdministrativeInformation";
import { LeadDetail } from "./sections/LeadDetail";
import { AssignedDealer } from "./sections/AssignedDealer";
import { AdditionalFields } from "./sections/AdditionalFields";
import { Button } from "../Button";
import { CustomMenu } from "../CustomMenu";
import { FloatingActionButtonMenu } from "../FloatingActionButtonMenu";

export const EditLeadForm = (): JSX.Element => {
  const { leadId } = useParams();
  const user = new User();

  const showReassingDealer = !user.HasForbiddenAccess(
    "Can See Reassign Dealer"
  );
  const showDealerInfo = !user.HasForbiddenAccess("Can See Dealer Info");
  const showCoveringDealers = !user.HasForbiddenAccess(
    "Can See Covering Dealers"
  );
  const showForwardLook = !user.HasForbiddenAccess("Can See Forward Look");
  const showEmailDealer = !user.HasForbiddenAccess("Can See Email Dealer");
  const showAssignedDealer =
    showReassingDealer ||
    showDealerInfo ||
    showCoveringDealers ||
    showForwardLook ||
    showEmailDealer;

  const showNotes = !user.HasForbiddenAccess("Can See Notes");

  const editReassignDealer = !user.HasForbiddenAccess(
    "Can Edit Reassign Dealer"
  );
  const editEmailDealer = !user.HasForbiddenAccess("Can Edit Email Dealer");

  const showLeadOutcome = !user.HasForbiddenAccess("Can See Lead Outcome");
  const showProductInterest = !user.HasForbiddenAccess(
    "Can See Product Interest"
  );
  const showSaleAmount = !user.HasForbiddenAccess("Can See Sale Amount");
  const showLeadDetail =
    showLeadOutcome || showProductInterest || showSaleAmount;

  const editLeadOutcome = !user.HasForbiddenAccess("Can Edit Lead Outcome");
  const editProductInterest = !user.HasForbiddenAccess(
    "Can Edit Product Interest"
  );
  const editApptDate = !user.HasForbiddenAccess("Can Edit Appt Date");
  const editApptTime = !user.HasForbiddenAccess("Can Edit Appt Time");
  const editSaleAmount = !user.HasForbiddenAccess("Can Edit Sale Amount");

  const {
    state,
    number,
    isLookForwardModalOpen,
    dropdownItems,
    isAddCoverageDialogOpen,
    isDealerInfoDialogOpen,
    snackbarState,
    dropdownProduct,
    timeSlots,
    formFields,
    slots,
    dropDonwNonBillReason,
    dropDownDealerAccess,
    dropdownNumber,
    dropdownSale,
    dropdownApptTimeRange,
    isLookForwardLoading,
    isLoading,
    handleLookForwardModalClose,
    handleLookForwardModalOpen,
    isNewFieldDialogOpen,
    handleNewFielDialogOpen,
    handleNewFielDialogClose,
    handleAddCoverageClose,
    handleAddCoverageOpen,
    handleDealerInfoClose,
    handleDealerInfoOpen,
    handleViewChangesDialogClose,
    handleViewChangesDialogOpen,
    onLookForward,
    onChange,
    onChangeCheckBox,
    onChangeSelect,
    onUpdate,
    onCancel,
    hideSnackbar,
    onChangeDate,
    onChangeAutocomplete,
    onChangeFormField,
    onChangeFormFieldSelect,
    isViewChangesDialogOpen,
    openMenuOptions,
    toggleMenuOptions,
    anchorRef,
  } = useEditLeadForm(parseInt(leadId!));

  const options = [
    {
      title: "Dealer Information",
      icon: <Search />,
      onClick: handleDealerInfoOpen,
    },
    {
      title: "Coverage Dealers",
      icon: <FormatListBulleted />,
      onClick: handleAddCoverageOpen,
    },
    {
      title: "Look Forward",
      icon: <Icon size={1} path={mdiMapMarkerRadiusOutline} />,
      onClick: handleLookForwardModalOpen,
    },
    {
      title: "View Changes",
      icon: <History />,
      onClick: handleViewChangesDialogOpen,
    },
    {
      title: "View UTM Data",
      icon: <BarChart />,
      onClick: () => null,
    },
    {
      title: "View Trusted Form Data",
      icon: <Handshake />,
      onClick: () => null,
    },
  ];

  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress />
      </Backdrop>
      <Card sx={{ borderRadius: 4, width: "100%" }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <Typography
              fontSize={22}
              fontWeight="600"
              color="#1BA0E9"
              textAlign="center"
            >
              Editing |{" "}
              <span style={{ color: "#00000099" }}>
                Lead ID# {leadId} ({state.actualLastName},{" "}
                {state.actualFirstname})
              </span>
            </Typography>
          </Box>

          <AssignedDealer
            dropdownDealerItems={dropDownDealerAccess}
            handleAddCoverageOpen={handleAddCoverageOpen}
            handleDealerInfoOpen={handleDealerInfoOpen}
            newDealerId={state.newDealerId}
            onChangeCheckBox={onChangeCheckBox}
            onLookForward={handleLookForwardModalOpen}
            sendEmail={state.sendEmail}
            formDisabled={state.formDisabled}
            onChangeSelect={onChangeSelect}
          />

          <PersonalInformation
            actualFirstName={state.actualFirstname}
            actualLastName={state.actualLastName}
            actualAddress={state.actualAddress}
            actualCity={state.actualCity}
            actualState={state.actualState}
            actualZip={state.actualZip}
            actualEmail={state.actualEmail}
            actualPhone={state.actualPhone}
            email2={state.email2}
            phone2={state.phone2}
            otherInfo={state.otherInfo}
            onChange={onChange}
          />

          <LeadDetail
            apptDate={state.apptDate}
            apptTime={state.apptTime}
            callStatus={state.callStatus}
            dropdownItems={dropdownItems}
            dropdownProductItems={dropdownProduct}
            dropdownApptTimeRange={dropdownApptTimeRange}
            onChangeDate={(newValue) => onChangeDate(newValue, "apptDate")}
            product={state.product}
            saleAmount={state.saleAmount}
            formDisabled={state.formDisabled}
            onChangeSelect={onChangeSelect}
          />

          <SaleInformation
            isLeadOutcomeSale={state.callStatus === "8"}
            saleStatus={state.saleStatus}
            dropdownSaleItems={dropdownSale}
            installDate={state.installDate}
            saleDate={state.saleDate}
            orderDate={state.orderDate}
            shipDate={state.shipDate}
            formDisabled={state.formDisabled}
            onChangeSelect={onChangeSelect}
            onChangeInstallDate={(newValue) =>
              onChangeDate(newValue, "installDate")
            }
            onChangeOrderDate={(newValue) =>
              onChangeDate(newValue, "orderDate")
            }
            onChangeSaleDate={(newValue) => onChangeDate(newValue, "saleDate")}
            onChangeShipDate={(newValue) => onChangeDate(newValue, "shipDate")}
          />
          <Box
            sx={{
              width: "100%",
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              mt: 4,
              p: 2,
            }}
          >
            <Stack direction="row" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" label="Cancel" onClick={onCancel} />
                <Button
                  disabled={state.formDisabled}
                  startIcon={<Save />}
                  label="Save"
                  onClick={onUpdate}
                />
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      <ViewChangesDialog<CallDetailHistory>
        entity="LEAD"
        id={leadId!}
        label="Lead"
        isOpen={isViewChangesDialogOpen}
        onClose={handleViewChangesDialogClose}
      />
      <LeadInfoDialog
        id={state.newDealerId.toString()}
        open={isDealerInfoDialogOpen}
        onClose={handleDealerInfoClose}
      />
      <EditLeadNewFieldDialog
        isOpen={isNewFieldDialogOpen}
        onClose={handleNewFielDialogClose}
      />
      <CoverageDealers
        productId={state.product}
        zipCode={state.actualZip}
        isOpen={isAddCoverageDialogOpen}
        onClose={handleAddCoverageClose}
      />
      <CustomSnackbar
        open={snackbarState.isOpen}
        variant="secondary"
        level={snackbarState.level as any}
        message={snackbarState.message}
        onClose={hideSnackbar}
      />
      <LookForwardModal
        timeSlots={timeSlots}
        slots={slots}
        zip={state.actualZip}
        dealerName={state.newDealerId.toString()}
        open={isLookForwardModalOpen}
        handleClose={handleLookForwardModalClose}
      />
      <FloatingActionButtonMenu options={options} />
    </Box>
  );
};
